import * as THREE from 'three';
import { Text } from 'troika-three-text';

var loadedFonts = {};
// @ts-ignore
import { dxfHelpers } from '../dxfHelpers';
import * as mathHelpers from '../../../utilities/math-helpers';
import { renderOrders } from '../../../../viewer/render-orders.ts';

export default class DxfTextParser {
  constructor() {
    // Create a shared material for all text objects (similar to GLB implementation)
    this.textMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide,
    });
  }

  /**
   * Takes the parser entity data and gets the relevant information for later processing
   * @param {*} entity entity to process
   * @param {*} properties
   * @param {*} transform wether or not to transform the coordinates, relevant if it's added by insert block.
   */
  processText(entity, properties, insert) {
    // Note: if boxsize is set it will strictly set the size of the text, otherwise it's determined by height parameter
    const boxSize = this.getBoxSize(entity, insert);

    // Note: we skip texts that too tiny, this avoids processing junk data and data that causes glitches easily.
    if (boxSize && (boxSize.height <= 0 || boxSize.width <= 0)) {
      return;
    }

    let position = entity.type === 'MTEXT' ? entity.position : entity.startPoint;

    if (boxSize) {
      position = entity.endPoint;
    }

    if (insert) {
      position = dxfHelpers.transformInsertVertex(insert, position);
    }

    let textHeight = boxSize ? boxSize.height : this.getTextSize(entity, insert);

    if (textHeight <= 0) {
      return;
    }

    let textRows = this.getTextRows(entity);

    if (!textRows) {
      return;
    }

    let rotation = this.getTextRotation(entity, insert);
    const attachmentPoint = this.getAttachmentPoint(entity);

    return {
      textRows: textRows,
      textHeight: textHeight,
      position: position,
      rotation: rotation,
      offset: this.getOffset(attachmentPoint),
      font: 'monospace',
      color: this.parseTextColor(properties.color),
      alignment: this.getTextAlignment(attachmentPoint),
      type: entity.type,
    };
  }

  /**
   * Gets the attachment point, this determins how the text is aligned vertically and horizontally.
   * It may be determined by attachemntpoint variable or set by vertical and horizontal justification parameters
   */
  getAttachmentPoint(entity) {
    if (entity.attachmentPoint) {
      return entity.attachmentPoint;
    }

    if (entity.verticalJustification && entity.verticalJustification <= 3) {
      // 1 = bottom, (index 0)
      // 2 = middle, (index 1)
      // 3 = top (index 2)
      let y = entity.verticalJustification - 1;

      // 0 = left,
      // 1 = center
      // 2 = right
      let x = entity.horizontalJustification || 0;
      if (x > 2) {
        x = 0;
      }

      const attachmentPointMap = [
        [7, 8, 9],
        [4, 5, 6],
        [1, 2, 3],
      ];

      return attachmentPointMap[y][x];
    }
  }

  getBoxSize(entity) {
    if (entity.verticalJustification && entity.startPoint && entity.endPoint) {
      return {
        width: Math.abs(entity.endPoint.x - entity.startPoint.x) / 2,
        height: Math.abs(entity.endPoint.y - entity.startPoint.y) / 2,
      };
    }
  }

  getTextRows(entity) {
    if (entity.text.includes('{') && entity.text.includes('}')) {
      entity.text = this.parseFontInfoRow(entity.text);
    }

    let textRows;
    if (entity.type === 'MTEXT') {
      textRows = entity.text.split('\\P');
    } else {
      textRows = [entity.text];
    }

    textRows = this.processSpecialFormatting(textRows);

    if ((textRows.length === 1 && textRows[0] === '') || textRows.length === 0) {
      return;
    }

    return textRows;
  }

  setMaterialIndex(faces, index) {
    faces.map((face) => {
      face.materialIndex = index;
    });
  }

  /**
   * transforms the coordinates so the group holds the main position and child entities have
   * positions cloeser to 0,0,0
   * @param {THREE.Group} textGroup
   */
  tranformTextCollection(textGroup) {
    if (textGroup.children.length > 0) {
      const offset = textGroup.children[0].position.clone() || new THREE.Vector3(0, 0, 0);
      offset.setZ(0);
      textGroup.position.set(offset.x, offset.y, offset.z);
      for (const child of textGroup.children) {
        child.position.sub(offset);
      }
    }
  }

  /** Generates a collection of Troika Text objects for the given text data */
  generateTextCollection(textData) {
    const textGroup = new THREE.Group();
    textGroup.name = 'TextCollection';

    for (let i = 0; i < textData.length; i++) {
      textGroup.add(this.generateTroikaText(textData[i]));
    }

    this.tranformTextCollection(textGroup);

    return textGroup;
  }

  /**
   * Generates a Troika Text object for the given text data
   * @param {object} textData - The text data object
   * @returns {Text} - Troika Text object
   */
  generateTroikaText(textData) {
    const text = new Text();

    // Join text rows with newlines
    const textContent = textData.textRows.join('\n');
    text.text = textContent;

    // Set font size based on text height with scaling factor (similar to GLB implementation)
    const sizeFactor = 1.33; // Increased from 1.33 to make text more visible
    text.fontSize = (textData.textHeight || 0.5) * sizeFactor;

    // Convert color from RGB string to hex (similar to GLB implementation)
    let colorHex = 0xffffff; // default white
    if (textData.color && textData.color.startsWith('rgb(')) {
      const rgbMatch = textData.color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1]);
        const g = parseInt(rgbMatch[2]);
        const b = parseInt(rgbMatch[3]);
        colorHex = (r << 16) | (g << 8) | b;
      }
    }
    text.color = colorHex;

    // Set anchor based on alignment
    switch (textData.alignment) {
      case 'center':
        text.anchorX = 'center';
        break;
      case 'right':
        text.anchorX = 'right';
        break;
      default:
        text.anchorX = 'left';
    }

    // Set vertical anchor to bottom baseline for consistency
    text.anchorY = 'bottom-baseline';

    // Set position (the coordinate transformation will be applied at the collection level)
    text.position.copy(textData.position);

    // Set rotation using setRotationFromAxisAngle (similar to GLB implementation)
    text.setRotationFromAxisAngle(new THREE.Vector3(0, 0, 1), textData.rotation || 0);

    // Set render order
    text.renderOrder = renderOrders.dxfAsset.text;

    // Set material properties for better rendering (similar to GLB implementation)
    text.material = this.textMaterial;

    // Sync the text to generate geometry
    text.sync();

    return text;
  }

  getTextAlignment(attachmentPoint) {
    if (attachmentPoint === 2 || attachmentPoint === 5 || attachmentPoint === 8) {
      return 'center';
    } else if (attachmentPoint === 3 || attachmentPoint === 6 || attachmentPoint === 9) {
      return 'right';
    } else {
      return 'left';
    }
  }

  getOffset(attachmentPoint) {
    switch (attachmentPoint) {
      case 1:
        return new THREE.Vector3(0.5, 0);
      case 2:
        return new THREE.Vector3(0, 0);
      case 3:
        return new THREE.Vector3(-0.5, 0);
      case 4:
        return new THREE.Vector3(0.5, 0.5);
      case 5:
        return new THREE.Vector3(0, 0.5);
      case 6:
        return new THREE.Vector3(-0.5, 0.5);
      case 7:
        return new THREE.Vector3(0.5, 1);
      case 8:
        return new THREE.Vector3(0, 1);
      case 9:
        return new THREE.Vector3(-0.5, 1);
    }
    return new THREE.Vector3(0.5, 1);
  }

  processSpecialFormatting(textRows) {
    let newRows = [];

    textRows.map((row) => {
      if (row[0] === '\\' && row.includes(';')) {
        let subRows = row.split(';');
        subRows.map((subrow) => {
          newRows.push(subrow);
        });
      } else {
        newRows.push(row);
      }
    });

    for (let rowIndex = newRows.length - 1; rowIndex >= 0; rowIndex--) {
      if (newRows[rowIndex].substring(0, 2) === '%%') {
        newRows[rowIndex] = newRows[rowIndex].substring(3);
      }

      if (newRows[rowIndex].substring(0, 2) === '\\F') {
        //File
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\A') {
        //Header (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\W') {
        //Width (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\T') {
        //Don't know (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\H') {
        //Don't know (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\S') {
        //Don't know (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 4) === '\\pxt') {
        //Don't know (?)
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 2) === '\\f') {
        //Font info, skip for now
        newRows.splice(rowIndex, 1);
      } else if (newRows[rowIndex].substring(0, 4) === '\\o\\l') {
        //Don't know (?)
        newRows[rowIndex] = newRows[rowIndex].substring(4);
      } else if (newRows[rowIndex].substring(0, 2) === '\\l') {
        //Don't know (?)
        newRows[rowIndex] = newRows[rowIndex].substring(2);
      } else if (newRows[rowIndex].substring(0, 2) === '\\o') {
        //Don't know (?)
        newRows[rowIndex] = newRows[rowIndex].substring(2);
      }
    }

    if (newRows.length === 0) {
      textRows = [''];
    }

    return newRows;
  }

  /** Special logif for rows encapsulated in {} eg {\fArial|b0|i0|c0|p34;25} */
  parseFontInfoRow(text) {
    //'\\A1;Växthus 40 m{\\H0.7x;\\S2^ ;}'

    let subtexts = [];
    //find first { index
    //push substring up until that point
    let prog = 0;
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '{') {
        if (i > prog) {
          let sub = text.substring(prog, i);
          if (sub.length > 0) {
            subtexts.push(sub);
          }
        }

        for (let j = i; j < text.length; j++) {
          if (text[j] === '}') {
            let funcsub = text.substring(i + 1, j);
            funcsub = [funcsub];
            funcsub = this.processSpecialFormatting(funcsub);
            funcsub.map((row) => {
              subtexts.push(row);
            });

            i = j;
            prog = j;
            break;
          }
        }
      }
    }

    let newText = '';
    subtexts.map((row) => {
      newText += row;
    });

    /* let fontInfo = split[0].split('|');
    var fontSize = 8;
    if (fontInfo.length === 4) {
        //magical number
        fontSize = fontInfo[4].slice(1, fontInfo[4].length);
    }
    var fontString = fontSize + 'px ' + fontInfo[0].slice(2, fontInfo[0].length); */

    return newText;
  }

  parseFont(context, textStyleName) {
    //Try to use isocp as default, if it's not availible use Monospace
    let font = this.doesFontExist(context, 'isocpeur') ? 'isocpeur' : 'monospace';

    //Try to load specific font if
    if (textStyleName) {
      if (this.doesFontExist(context, textStyleName)) {
        font = textStyleName;
      } else {
        console.log('Warning missing font: ' + textStyleName);
      }
    }

    return font;
  }

  doesFontExist(context, fontName) {
    // the text whose final pixel size I want to measure
    var text = 'abcdefghijklmnopqrstuvwxyz0123456789';

    // specifying the baseline font
    context.font = '72px monospace';

    // checking the size of the baseline text
    var baselineSize = context.measureText(text).width;

    // specifying the font whose existence we want to check
    context.font = "72px '" + fontName + "', monospace";

    // checking the size of the font we want to check
    var newSize = context.measureText(text).width;

    //
    // If the size of the two text instances is the same, the font does not exist because it is being rendered
    // using the default sans-serif font
    //
    if (newSize === baselineSize) {
      return false;
    } else {
      return true;
    }
  }

  /** Converts color from decimal to rgb */
  parseTextColor(color) {
    if (color < 0) {
      color = 0xffffffff + color + 1;
    }

    let hex = color.toString(16).toUpperCase();

    if (hex.length === 4) {
      hex = '00' + hex;
    }
    if (hex.length === 2) {
      hex = '0000' + hex;
    }
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    let rgb = result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };

    return 'rgb(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ')';
  }

  getTextSize(entity, insert) {
    const scale = insert && insert.yScale ? insert.yScale : 1;

    if (entity.textHeight) {
      return entity.textHeight * scale;
    }

    if (entity.height) {
      return entity.height * scale;
    }

    return 1;
  }

  getTextRotation(entity, insert) {
    const insertRoation = insert && insert.rotation ? insert.rotation : 0;

    if (entity.rotation) {
      return mathHelpers.degreesToRadians((entity.rotation + insertRoation) % 360);
    }

    if (entity.directionVector) {
      const vecX = entity.directionVector.x;
      const vecY = entity.directionVector.y;
      const entityRotation = mathHelpers.radiansToDegrees(Math.atan2(vecY, vecX));

      return mathHelpers.degreesToRadians((entityRotation + insertRoation) % 360);
    }

    return mathHelpers.degreesToRadians(insertRoation);
  }

  createTextEntityFromAttdef(attdef, attribs) {
    if (!(attribs && attribs.length > 0)) {
      console.error('ATTDEF identified, but there was no corresponding ATTRIB', attdef);
      return attdef;
    }

    //Get next attrib in sequence
    const attrib = attribs.shift();

    if (attrib.type !== 'ATTRIB') {
      console.error('Unexpected entity in INSERT', attdef, attrib);
      return attdef;
    }

    if (attrib.invisible) {
      return attdef;
    }

    let startPoint;
    let endPoint;
    if (attrib.verticalJustification && attrib.startPoint && attrib.endPoint) {
      startPoint = attrib.startPoint;
      endPoint = attrib.endPoint;
    } else if (attdef.verticalJustification && attdef.startPoint && attdef.endPoint) {
      startPoint = attdef.startPoint;
      endPoint = attdef.endPoint;
    }

    //Define new entity with correct information, attrib overrides attdef, or do they merge?
    return {
      type: 'TEXT',
      scale: attrib.scale !== undefined ? attrib.scale : attdef.scale,
      textStyle: attrib.textStyle !== undefined ? attrib.textStyle : attdef.textStyle,
      layer: attrib.layer !== undefined ? attrib.layer : attdef.layer,
      colorIndex: attrib.colorIndex !== undefined ? attrib.colorIndex : attdef.colorIndex,
      color: attrib.color !== undefined ? attrib.color : attdef.color,
      startPoint:
        startPoint || (attrib.startPoint !== undefined ? attrib.startPoint : attdef.startPoint),
      endPoint: endPoint || (attrib.endPoint !== undefined ? attrib.endPoint : attdef.endPoint),
      textHeight: attrib.textHeight !== undefined ? attrib.textHeight : attdef.textHeight,
      text: attrib.text !== undefined ? attrib.text : attdef.text,
      rotation: attrib.rotation !== undefined ? attrib.rotation : attdef.rotation,
      prompt: attdef.prompt,
      tag: attdef.tag,
      constant: attdef.constant,
      verificationRequired: attdef.verificationRequired,
      preset: attdef.preset,
      verticalJustification: attrib.verticalJustification || attdef.verticalJustification,
      horizontalJustification: attrib.horizontalJustification || attdef.horizontalJustification,
    };
  }
}

const textwordparser = {
  textparser: new DxfTextParser(),

  /**
   *
   * @param {any} textData
   * @returns {Text}
   */
  generateMesh(textData) {
    return this.textparser.generateTroikaText(textData);
  },

  /**
   * Returns the amount of rows for a text object
   * @param {*} textData
   */
  getTextRows(textData) {
    return textData.textRows;
  },

  /**
   * Returns the height for a text object
   * @param {*} textData
   */
  getTextHeight(textData) {
    return textData.textHeight;
  },

  /**
   * Returns the width for a text object (estimated)
   * @param {*} textData
   */
  getTextWidth(textData) {
    // For Troika Text, we can estimate width based on character count and font size
    const maxRowLength = Math.max(...textData.textRows.map((row) => row.length));
    return maxRowLength * textData.textHeight * 0.6; // Rough estimation
  },
};

export { loadedFonts, textwordparser };
